import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { SalesService, CompanyResponse, BrandsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FormGroup } from '@angular/forms';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Dialog } from 'primeng/dialog';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides, FiltersPanelDropdownLazyLoadEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';

@Component({
    selector: 'app-sales',
    templateUrl: './sales.component.html',
    styleUrl: './sales.component.scss',
    host: {
        class: 'app-sales'
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SalesComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly salesTableName = TableSettingService.SalesTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: isReturned
  private returnedOptions: Array<{ id: string | number; text: string }> = [];
 
  // Dropdown: brands
  brands = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  @ViewChild('returnSaleDialog') returnSaleDialog!: Dialog;
  exports: boolean = false;
  allowCreateSale: boolean = false;
  currentDate: Date = new Date();
  maxDate: Date = new Date();
  minDate: Date = new Date();
  isCalendarEnabled: boolean = false;

  displayExportModalbox: boolean = false;
  selectedSaleSerialNumber: string | undefined;

  filterOperator = FilterOperator;

  company: CompanyResponse = {
    inventorySubmissionThresholdDays: 0
  };

  constructor(
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    public permissionService: PermissionService,
    public override tableSettingService: TableSettingService,
    private exportService: ExportService,
    private translateService: TranslateService,
    private session: SessionService,
    private toasterService: ToasterService,
    private salesService: SalesService,
    private brandService: BrandsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.company = await this.session.getCompanyData();
    await this.tableSettingService.init();

    // i18n propagation on lang change
    this.rebuildIsReturnOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildIsReturnOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
        this.changeDetector.markForCheck();
      });

    await super.setCache(this.salesTableName);

    this.initSortPanel({
      // display recently updated 1st
      defaultSort: { key: 'date', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();
    this.changeDetector.markForCheck();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_sales);
    this.allowCreateSale =
      await this.permissionService.hasActionPermissions(Actions.create_sales) && !this.company.implicitSaleCreation;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.salesService.apiSalesFilterTablePost(this.filter));
    this.data = this.page.results;

    this.changeDetector.markForCheck();
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
    this.changeDetector.markForCheck();
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.salesTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.salesTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  private rebuildIsReturnOptions(): void {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.returnedOptions = pipe.values;
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    } this.sortableColumns = nextSortableColumns;
  }

  goToCreateSaleForm() {
    this.router.navigate(['/sales/add']);
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      isReturned: {
        fieldLabel: 'TABLE.HEADER.Returned',
        isMultiselect: true,

        options: this.returnedOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      'item.product.brandId': {
        fieldLabel: 'TABLE.HEADER.Brand',
        isMultiselect: true,

        options: this.brands.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.brands.totalCount ?? 0,
        totalPages: this.brands.totalPages ?? 1,
        pageIndex: this.brands.pageIndex ?? 1,
        hasPreviousPage: this.brands.hasPreviousPage ?? false,
        hasNextPage: this.brands.hasNextPage ?? false
      }
    };
  }

    async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      if (!evt) {
        return;
      }
  
      // fetch dropdowns remote data
      const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
        'item.product.brandId': (e) => this.loadBrandsCombo(e)
      };
  
      const handler = handlers[evt.fieldKey];
      if (!handler) {
        return;
      }
  
      await handler(evt);
    }

  goToSaleDetails(event: any, saleId: any) {
    this.router.navigate([`/sales/${saleId}`]);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.salesService.apiSalesExportPost.bind(this.salesService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  private async loadBrandsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
      const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
        pageIndex: evt.pageIndex,
        pageSize: evt.pageSize,
        search: evt.search
      });
  
      const response = await firstValueFrom(
        this.brandService.apiBrandsFilterComboPost(requestPayload)
      );
  
      this.brands = normalizeDropdownState(response as any, evt.pageIndex) as any;
      this.rebuildFiltersPanelOverrides();
    }
}
